import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * @param {Object} [options] - Additional section morph options.
   * @param {boolean} [options.injectStylesheet]
   */
  async #refreshCartSection(options = {}) {
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const isExternalCartUpdate = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        if (!isExternalCartUpdate) return;

        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    // OUTGLOW fix B09/B11 (21/10/2026): a tela do carrinho ficava velha quando a rede voltava ou
    // quando o cliente voltava de outra aba; aqui ela é redesenhada com o carrinho do servidor.
    window.addEventListener('online', this.#syncWithServer);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    // OUTGLOW fix B28 (21/10/2026): captura no window roda antes da delegação do Component
    // (document, capture) e do setQuantity do Horizon, que trocava "abc", negativo e 0 por 1.
    window.addEventListener('blur', this.#handleQuantityInputBlur, { capture: true });
    window.addEventListener('keydown', this.#handleQuantityInputKeydown, { capture: true });
    this.#updateCartQuantitySelectorButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    // OUTGLOW fix B09/B11/B28 (21/10/2026): ver connectedCallback.
    window.removeEventListener('online', this.#syncWithServer);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('blur', this.#handleQuantityInputBlur, { capture: true });
    window.removeEventListener('keydown', this.#handleQuantityInputKeydown, { capture: true });
  }

  /**
   * OUTGLOW fix B28 (21/10/2026): teto de quantidade por linha do carrinho. Sem ele, 999 e 1000
   * entravam no carrinho e a bolinha do cabeçalho ficava vazia a partir de 100 itens.
   */
  static MAX_LINE_QUANTITY = 99;

  /** OUTGLOW fix B09 (21/10/2026): mensagem da linha quando a Shopify não responde (sem rede, 429, 5xx). */
  static UPDATE_FAILED_MESSAGE = "We couldn't update your cart. Check your connection and try again.";

  /** @type {number | null} */
  #hiddenAt = null;

  /**
   * OUTGLOW fix B11 (21/10/2026): ao voltar para a aba (depois de >= 2 s fora), redesenha o carrinho.
   * Com duas abas abertas, a gaveta velha mostrava linhas que já não batiam com o servidor.
   */
  #handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.#hiddenAt = Date.now();
      return;
    }
    const hiddenFor = this.#hiddenAt === null ? 0 : Date.now() - this.#hiddenAt;
    this.#hiddenAt = null;
    if (hiddenFor >= 2000) this.#syncWithServer();
  };

  /**
   * OUTGLOW fix B09/B11 (21/10/2026): redesenha a seção com o carrinho do servidor (mesma
   * chamada do pageshow do Horizon). Não roda com uma alteração em andamento, para não
   * atropelar a resposta dela.
   */
  #syncWithServer = () => {
    if (!this.dataset.sectionId || CartItemsComponent.#pendingQuantityUpdates.size > 0) return;
    if (!navigator.onLine) return;

    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        this.#updateCartQuantitySelectorButtonStates();
        this.#syncCartBubble();
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] sync render failed:', error);
      });
  };

  /**
   * OUTGLOW fix B09/B11 (22/10/2026): depois do #syncWithServer a gaveta mostrava o carrinho novo e a
   * bolinha do cabeçalho continuava com o número velho até a próxima ação. Lê a contagem do redesenho.
   */
  #syncCartBubble() {
    const countText = this.querySelector('[ref="cartItemCount"]')?.textContent;
    let count = countText ? parseInt(countText, 10) : NaN;
    if (Number.isNaN(count) && !this.querySelector('#cart-form') && this.querySelector('.cart-items__empty-button')) {
      count = 0;
    }
    if (!Number.isInteger(count) || count < 0) return;
    for (const icon of document.querySelectorAll('cart-icon')) {
      /** @type {any} */ (icon).renderCartBubble?.(count, false);
    }
  }

  /**
   * OUTGLOW fix B28 (22/10/2026): Enter ("Ir" no teclado do celular) no campo de quantidade enviava o
   * formulário do carrinho direto para o checkout com o número digitado (150), sem passar pelo teto.
   * Agora o Enter só confirma o campo: tira o foco, e o blur abaixo aplica as regras e atualiza a linha.
   * @param {KeyboardEvent} event
   */
  #handleQuantityInputKeydown = (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'updates[]' || !this.contains(input)) return;
    event.preventDefault();
    input.blur();
  };

  /**
   * OUTGLOW fix B28 (21/10/2026): trata o campo de quantidade ao sair dele, antes do Horizon.
   * - letras, vazio, decimal ou negativo: volta ao valor anterior (o do servidor) e não envia nada;
   * - 0: remove a linha (o Horizon trocava por 1);
   * - acima de 99: fica em 99.
   * @param {FocusEvent} event
   */
  #handleQuantityInputBlur = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'updates[]' || !this.contains(input)) return;

    const raw = input.value.trim();
    const parsed = Number(raw);
    const isInvalid = input.validity.badInput || raw === '' || !Number.isInteger(parsed) || parsed < 0;

    if (isInvalid || parsed === 0) {
      // Impede o setQuantity do Horizon; por isso o marcador que ele limparia é limpo aqui.
      event.stopPropagation();
      input.removeAttribute('data-skip-value-update');

      if (isInvalid) {
        input.value = input.defaultValue;
        this.#updateCartQuantitySelectorButtonStates();
        return;
      }

      const line = Number(input.dataset.cartLine);
      if (line) this.onLineItemRemove(line);
      return;
    }

    // Só quando o cliente digitou: uma linha que já tem 120 (pelo + ou pela página do produto) não cai
    // para 99 só porque o campo recebeu e perdeu o foco, nem ao tocar em CHECK OUT com o campo focado.
    if (parsed > CartItemsComponent.MAX_LINE_QUANTITY && raw !== input.defaultValue.trim()) {
      input.value = String(CartItemsComponent.MAX_LINE_QUANTITY);
    }
  };

  /**
   * Re-renders the cart section when the page is restored from the back/forward cache.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event, which is why the bubble and the cart body disagree until a reload.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of
   * `shopify-account > shop-login`, and every product page carries one. A shopper's Back there
   * rebuilds the document from the HTTP disk cache instead: `persisted` is false and the frozen
   * cart markup renders anyway. Only the navigation entry separates that from an ordinary page
   * view, so it is the second channel. Safari and Firefox do restore the same page and arrive
   * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
   *
   * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in
   * Chrome and Safari, reporting `navigate`, so `back_forward` alone would miss every real
   * restore. And `pageshow` fires after `load` on every ordinary navigation, so dropping both
   * guards would put a section fetch on every page view.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId`
    // getter throws without it. Every other caller reads it inside a promise chain that
    // already has a catch; this one runs straight off a window listener, so it would escape.
    if (!this.dataset.sectionId) return;

    // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache
    // still holds the pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache
    // from the stale document it just rebuilt. Either way the cached copy is the same stale HTML
    // the DOM already shows.
    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        // Morph swaps the quantity selectors in place, so they never reconnect and their
        // min/max button states stay bound to the pre-restore quantities. A line restored to its
        // minimum would otherwise keep minus enabled: a wrong affordance and a wasted cart mutation.
        this.#updateCartQuantitySelectorButtonStates();
        // No drawer-open event fires on a restore, so the sticky summary measurement has to
        // be driven from here. `cart-drawer-component` listens for this.
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // OUTGLOW fix B09 (21/10/2026): o Horizon esvaziava a tela (ou apagava a linha do DOM) antes da
    // resposta. Sem rede ou com 429 a gaveta ficava "Your cart is empty" com o item ainda no
    // carrinho. Agora a linha só recolhe (animação reversível); o estado vazio entra depois do
    // sucesso e, no erro, a linha volta com a mensagem.
    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];

    const rowsToRemove = cartItemRowToRemove
      ? [
          cartItemRowToRemove,
          // Get all nested lines of the row to remove
          ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
        ]
      : [];

    // If the cart item row is the last row, trigger the cart empty state once the server confirms
    const isEmptyCart = rowsToRemove.length > 0 && rowsToRemove.length == this.refs.cartItemRows.length;
    const template = document.getElementById('empty-cart-template');

    const showEmptyCart =
      isEmptyCart && template instanceof HTMLTemplateElement
        ? () =>
            startViewTransition(() => {
              document.getElementById('cart-drawer-heading')?.remove();
              this.replaceChildren(document.importNode(template.content, true));
            }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page'])
        : undefined;

    const restoreRows = () => {
      for (const row of rowsToRemove) {
        row.classList.remove('removing');
        row.style.removeProperty('--row-height');
        row.style.removeProperty('display');
      }
    };

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      onSuccess: showEmptyCart,
      onError: restoreRows,
    });

    // Add class to the row to trigger the animation (the row stays in the DOM until the server answers)
    rowsToRemove.forEach((row) => {
      if (prefersReducedMotion()) return row.style.setProperty('display', 'none');

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {() => unknown} [config.onSuccess] - OUTGLOW: runs after the server confirms, before the morph.
   * @param {() => void} [config.onError] - OUTGLOW: runs when the update fails, before the line error shows.
   * @returns {Promise<boolean>} OUTGLOW: whether the server applied the change.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    // OUTGLOW fix B09 (21/10/2026): a falha já aparece na linha; sem este catch vazio a rejeição
    // (quando nenhum outro componente escuta) virava "Uncaught (in promise)" no console.
    deferredUpdatePromise.promise.catch(() => {});
    let completeQuantityUpdate = () => {};
    /** @type {Promise<void>} */
    const quantityUpdate = new Promise((resolve) => {
      completeQuantityUpdate = () => resolve();
    });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);

    // OUTGLOW fix B11 (21/10/2026): a key da linha identifica o item; o número da linha muda quando
    // outra aba adiciona ou remove algo, e a gaveta velha alterava (ou removia) o produto errado.
    const lineKey = this.refs.cartItemRows?.[config.line - 1]?.dataset.key ?? '';
    // OUTGLOW fix B09 (21/10/2026): depois do OK da Shopify, um erro no redesenho não pode virar
    // "falhou" (a tela voltaria ao valor velho com o servidor já alterado).
    let serverApplied = false;

    try {
      this.#disableCartItems();

      const { line, quantity } = config;
      const { cartTotal } = this.refs;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      }

      for (const sectionId of sectionsToUpdate) {
        sectionRenderer.abortRender(sectionId);
      }

      const body = JSON.stringify({
        // OUTGLOW fix B11 (21/10/2026): {id: key} em vez de {line: N}; o número só fica de reserva.
        ...(lineKey ? { id: lineKey } : { line: line }),
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: lineKey, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      // OUTGLOW fix B09 (21/10/2026): o 429 da Shopify vem em HTML (desafio); o JSON.parse quebrava
      // e o catch deixava o campo com o valor novo e o total em shimmer.
      let parsedResponseText = null;
      try {
        parsedResponseText = JSON.parse(responseText);
      } catch {
        parsedResponseText = null;
      }

      resetShimmer(this);

      // OUTGLOW fix B09 (21/10/2026): conferir response.ok. Antes só "errors" contava como falha,
      // e um 429/5xx seguia como se a quantidade nova tivesse entrado.
      if (!response.ok || !parsedResponseText || parsedResponseText.errors) {
        const serverMessage =
          (typeof parsedResponseText?.errors === 'string' && parsedResponseText.errors) ||
          (typeof parsedResponseText?.description === 'string' && parsedResponseText.description) ||
          '';
        const message = serverMessage || CartItemsComponent.UPDATE_FAILED_MESSAGE;
        deferredUpdatePromise.reject(new Error(message));

        // OUTGLOW fix B11 (21/10/2026): 400/422 = a linha mudou ou não existe mais (outra aba) ou
        // não há estoque: redesenha com o carrinho do servidor antes de mostrar o erro. A bolinha do
        // cabeçalho acompanha o redesenho (o evento do Horizon foi rejeitado e não a atualiza).
        if (response.status === 400 || response.status === 422) {
          await this.#refreshCartSection();
          this.#syncCartBubble();
        }

        config.onError?.();
        // 400 traz texto técnico ("no valid id or line parameter"): usa a mensagem genérica.
        this.#showLineError(lineKey, line, response.status === 400 ? CartItemsComponent.UPDATE_FAILED_MESSAGE : message);
        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: response.status === 400 || response.status === 422 ? 'INVALID' : 'SERVICE_UNAVAILABLE',
          })
        );
        return false;
      }

      serverApplied = true;
      await config.onSuccess?.();

      const sections = /** @type {Record<string, string> | null | undefined} */ (parsedResponseText.sections);
      const sectionHTML = sections?.[this.sectionId];
      const parsedItemCount = Number(parsedResponseText.item_count);
      let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      if (!sectionHTML) {
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: sections ?? undefined,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await this.#refreshCartSection();
        return true;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : newCartItemCount;

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: sections ?? undefined,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      sectionRenderer.abortRender(this.sectionId);
      await morphSection(this.sectionId, sectionHTML, {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      this.#updateCartQuantitySelectorButtonStates();
      return true;
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);

      // OUTGLOW fix B09 (21/10/2026): sem rede o fetch rejeita e o Horizon deixava o campo com o valor
      // novo e o total em shimmer. Agora volta ao valor do servidor e mostra o erro na linha. Se a
      // Shopify já tinha aplicado a mudança, só redesenha.
      if (serverApplied) {
        await this.#refreshCartSection();
        return true;
      }
      resetShimmer(this);
      config.onError?.();
      this.#showLineError(lineKey, config.line, CartItemsComponent.UPDATE_FAILED_MESSAGE);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      return false;
    } finally {
      try {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      } finally {
        CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
        completeQuantityUpdate();
      }
    }
  }

  /**
   * OUTGLOW fix B09/B11 (21/10/2026): substitui o #handleCartError do Horizon. Acha a linha pela key
   * (o número pode ter mudado depois de um redesenho), volta o campo ao valor do servidor, tira o
   * shimmer e mostra a mensagem no aviso da linha (role="alert"). Não lança erro se a linha sumiu.
   * @param {string} lineKey - The line item key.
   * @param {number} line - The line number (fallback when there is no key).
   * @param {string} message - The message to show.
   */
  #showLineError(lineKey, line, message) {
    resetShimmer(this);

    const rows = this.refs.cartItemRows ?? [];
    const row = lineKey ? rows.find((r) => r.dataset.key === lineKey) : rows[line - 1];
    if (row) {
      const quantityInput = row.querySelector('input[name="updates[]"]');
      if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;

      const errorContainer = row.querySelector('[ref^="cartItemErrorContainer-"]');
      const errorText = row.querySelector('[ref^="cartItemError-"]');
      if (errorContainer instanceof HTMLElement && errorText instanceof HTMLElement) {
        errorText.textContent = message;
        errorContainer.classList.remove('hidden');
      }
    }

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          // Apply confirmed cart HTML directly; page-capture and row-entry animations
          // delayed the visible result of an add, especially on mobile.
          await morphSection(this.sectionId, cartItemsHtml, morphOptions);

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection(morphOptions);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    // Discounts such as Buy X Get Y can split one variant across several cart lines.
    /** @type {Map<string, number>} */
    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) ?? 0) + item.quantity);
    }

    for (const [variantId, quantity] of quantitiesByVariant) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      // OUTGLOW fix B28 (21/10/2026, revisado): o teto de 99 NÃO usa o atributo max nativo. O campo fica
      // dentro de form#cart-form e o CHECK OUT é o submit desse form: com max=99, uma linha que já tem mais
      // de 99 no servidor (possível pela página do produto) deixava o form inválido e bloqueava o checkout.
      // O teto vale só para o que o cliente digita (ver o tratamento no blur, acima).
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`, and the DOM
   * lib has no per-string overload for `'navigation'`. `entryType` is the spec's discriminant and
   * lives on the base, so this narrows at runtime instead of asserting the way a cast would.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
